import { Select as AntdSelect } from "antd";
import type {
  DefaultOptionType,
  SelectProps as AntdSelectProps,
} from "antd/es/select";
import styled, { createGlobalStyle, css } from "styled-components";
import { COLORS, DARK_THEME_COLORS, LIGHT_THEME_COLORS } from "styles/colors";
import { useAppSelector } from "redux/hooks";
import { useLocation } from "react-router-dom";
import { FaCaretDown } from "react-icons/fa";
import { calcRem } from "styles/theme";
import { FLEX } from "styles/flex";
import { IoIosClose } from "react-icons/io";

export const DropdownIndicator = ({
  color,
}: {
  color?: string;
  themeMode?: string;
}) => {
  return <FaCaretDown color={color} />;
};

const RenderWrapper = styled.div`
  ${FLEX.FlexStartCenter};
  gap: 4px;
  width: 100%;
  position: relative;

  input[type="checkbox"] {
    margin-top: 0;
    min-width: 16px;
    min-height: 16px;
  }
`;

const OptionLabel = styled.span`
  flex-grow: 1;
  min-width: 0;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  font-size: 12px;
`;

export const defaultMultiOptionRender = (
  option: any,
  selectedValues: Array<string | number> = []
) => {
  const checked = () => {
    if (option?.value !== null) {
      return selectedValues.includes(option.value);
    }
    return false;
  };

  return (
    <RenderWrapper className="mydw-select-option-render">
      <input type="checkbox" checked={checked()} readOnly={true} />
      <OptionLabel>{option?.label}</OptionLabel>
    </RenderWrapper>
  );
};

export enum SelectVariant {
  DEFAULT = "default",
  DASHBOARD = "dashboard",
  ALERT = "alert",
}

export type CustomSelectProps<ValueType = any> = AntdSelectProps<
  ValueType,
  DefaultOptionType
> & {
  className?: string;
  mydwVariant?: SelectVariant;
  showOptionFullText?: boolean; //option 명 다 보이는 property
};

const SelectDropdownStyle = createGlobalStyle`
  .mydw-select-dropdown {
    z-index: 9999;
    font-size: ${calcRem(14)};
  }

  .mydw-select-dropdown.mydw-select-dropdown--dashboard {
    z-index: 78;
    font-size: ${calcRem(12)};
  }

  /* Option list polish (matches myDW legacy look) */
  .mydw-select-dropdown .ant-select-item {
    padding: 0;
  }

  .mydw-select-dropdown .ant-select-item-option {
    margin: 2px 4px;
    padding: 6px 10px;
    border-radius: 2px;
  }

  /* Hide antd default right-side selected check icon */
  .mydw-select-dropdown .ant-select-item-option-state {
    display: none;
  }

  .mydw-select-dropdown .ant-select-item-option-content {
    line-height: ${calcRem(18)};
  }

  /* Optional: allow option label wrapping (no ellipsis) */
  .mydw-select-dropdown.mydw-select-dropdown--full-text-options .ant-select-item-option-content {
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  

  /* Selected option - light mode */
  /* .mydw-select-dropdown--light .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
    background: ${COLORS.BUTTONS1};
    border-color: ${COLORS.BUTTONS1};
  }
  .mydw-select-dropdown--light .ant-select-item-option-selected:not(.ant-select-item-option-disabled) .ant-select-item-option-content {
    color: ${COLORS.WHITE};
  } */

  /* Selected option - dark mode (keep background at COLORS.BUTTONS2, adjust layout like design) */
  .mydw-select-dropdown--dark .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
    background: ${COLORS.BUTTONS2};
    border-color: ${COLORS.BUTTONS2};
  }
  .mydw-select-dropdown--dark .ant-select-item-option-selected:not(.ant-select-item-option-disabled) .ant-select-item-option-content {
    color: ${COLORS.WHITE};
  }

  /* Hover/active consistency */
  .mydw-select-dropdown--dark .ant-select-item-option-active:not(.ant-select-item-option-disabled):not(.ant-select-item-option-selected) {
    background: ${DARK_THEME_COLORS.DISABLED};
  }
  .mydw-select-dropdown--light .ant-select-item-option-active:not(.ant-select-item-option-disabled):not(.ant-select-item-option-selected) {
    background: ${LIGHT_THEME_COLORS.LIGHTGRAY};
  }

  .mydw-select-dropdown .plus {
    display: inline-flex;
    align-items: center;
    height: 20px;
    padding: 0 6px;
    border-radius: 2px;
    border: 1px solid ${LIGHT_THEME_COLORS.BORDER};
    background: ${COLORS.WHITE};
    font-size: 12px;
    line-height: 1;
  }
`;

const StyledSelect = styled(AntdSelect)<{
  $themeMode: "light" | "dark" | string;
  $variant: SelectVariant;
}>`
  width: 100%;

  /* Antd default sets .ant-select-single { height: 32px; } - unset it so our selector height rules win */
  &.ant-select-single {
    height: unset !important;
    min-height: unset !important;
  }

  &.select-container {
    width: 100%;
  }

  .ant-select-selector {
    height: 38px !important;
    min-height: 38px !important;
    border-radius: 2px !important;
    padding-left: 10px !important;
    padding-right: 6px !important;
    box-shadow: none !important;
    border: 1px solid #cacaca !important;
    background: ${COLORS.WHITE} !important;
    align-items: center !important;
  }

  /* Leave room for clear(×) + arrow icons in multi/tags mode */
  &.ant-select-multiple .ant-select-selector {
    padding-right: 30px !important;
  }

  /* Move the clear button left so it doesn't hug the right border / overlap arrow */
  .ant-select-clear {
    inset-inline-end: 25px;
    top: 50%;
    transform: translateY(-50%);
  }

  .ant-select-selection-search-input {
    padding-left: 10px !;
  }

  .ant-select-selection-placeholder {
    color: ${(p) =>
      p.$variant === "dashboard" ? COLORS.BLACK : "#abb5c5"} !important;
    font-size: ${(p) =>
      p.$variant === "dashboard" ? calcRem(13) : calcRem(14)} !important;
    line-height: 18px;
    padding-left: 16px;
  }

  .ant-select-selection-item {
    font-size: ${(p) =>
      p.$variant === "dashboard" ? calcRem(13) : calcRem(14)} !important;
    font-weight: normal;
    line-height: 18px;
    color: ${COLORS.BLACK} !important;
  }

  .ant-select-arrow {
    inset-inline-end: 6px;
    color: ${COLORS.BUTTONS1};
  }

  ${(p) =>
    p.$variant === "alert" &&
    css`
      max-width: 280px;

      .ant-select-selector {
        background-color: ${p.$themeMode === "light"
          ? LIGHT_THEME_COLORS.LIGHTGRAY
          : COLORS.PRIMARY2} !important;
        border-color: ${p.$themeMode === "light"
          ? "#cacaca"
          : "#828c9e"} !important;
        min-height: 32px !important;
        height: 32px !important;
      }

      .ant-select-selection-item {
        font-size: 12px !important;
        color: ${p.$themeMode === "light"
          ? COLORS.BLACK
          : COLORS.WHITE} !important;
      }

      .ant-select-selection-placeholder {
        font-size: 12px !important;
        color: ${p.$themeMode === "light" ? "#6e7580" : "#ababab"} !important;
      }

      &.ant-select-disabled {
        .ant-select-selector {
          border-color: ${p.$themeMode === "light"
            ? LIGHT_THEME_COLORS.BORDER
            : DARK_THEME_COLORS.BORDER} !important;
        }

        .ant-select-selection-item,
        .ant-select-selection-placeholder {
          color: ${LIGHT_THEME_COLORS.BORDER} !important;
        }
      }
    `}

  &.ant-select-disabled {
    .ant-select-selector {
      ${(p) =>
        p.$themeMode === "light"
          ? css`
              background: ${LIGHT_THEME_COLORS.DISABLED} !important;
              border-color: ${LIGHT_THEME_COLORS.DISABLED_BORDER} !important;
            `
          : css`
              background: ${DARK_THEME_COLORS.DISABLED} !important;
              border-color: ${DARK_THEME_COLORS.DISABLED_BORDER} !important;
            `}
    }

    .ant-select-selection-item,
    .ant-select-selection-placeholder {
      ${(p) =>
        p.$themeMode === "light"
          ? css`
              color: ${COLORS.BLACK}60 !important;
            `
          : css`
              color: ${DARK_THEME_COLORS.LIGHTGRAY} !important;
            `}
    }

    .ant-select-arrow {
      ${(p) =>
        p.$themeMode === "light"
          ? css`
              color: ${COLORS.BLACK}60 !important;
            `
          : css`
              color: ${DARK_THEME_COLORS.LIGHTGRAY} !important;
            `}
    }
  }

  &:not(.ant-select-disabled).ant-select-open {
    .ant-select-selector {
      border-color: ${(p) =>
        p.$themeMode === "light" ? COLORS.BUTTONS1 : COLORS.TOGGLE} !important;
    }
  }

  &:not(.ant-select-disabled) {
    .ant-select-selector:hover {
      border-color: ${(p) =>
        p.$themeMode === "light" ? COLORS.BUTTONS1 : COLORS.TOGGLE} !important;
    }
  }
`;

const resolveMydwVariant = (
  variant: CustomSelectProps["mydwVariant"],
  isDashboard: boolean
): SelectVariant => {
  if (variant === SelectVariant.ALERT) return SelectVariant.ALERT;

  return isDashboard ? SelectVariant.DASHBOARD : SelectVariant.DEFAULT;
};

const getDefaultArrowColor = ({
  variant,
  disabled,
  theme,
}: {
  variant: SelectVariant;
  disabled: boolean;
  theme: any;
}) => {
  if (variant !== SelectVariant.ALERT) return COLORS.BLACK;
  if (disabled) {
    return theme === "light"
      ? LIGHT_THEME_COLORS.BORDER
      : DARK_THEME_COLORS.BORDER;
  }
  return theme === "light" ? COLORS.BLACK : COLORS.WHITE;
};

const SelectComponent = (props: CustomSelectProps) => {
  const { mydwVariant, ...rest } = props;
  const theme = useAppSelector((state) => state.theme.theme);
  const location = useLocation();
  const isDashboard: boolean = location.pathname.includes("dashboard/chart");
  const resolvedVariant = resolveMydwVariant(props.mydwVariant, isDashboard);
  const loading = props.loading ?? false;
  const noOption = (props.options || []).length === 0;
  const placeholder = props.placeholder ?? "Select";
  const disabled = props.disabled ?? (!loading && noOption);
  const showArrow = !disabled && (!noOption || loading);
  const defaultArrowColor = getDefaultArrowColor({
    variant: resolvedVariant,
    disabled,
    theme,
  });
  const suffixIcon =
    props.suffixIcon ??
    (showArrow ? <DropdownIndicator color={defaultArrowColor} /> : null);
  const isMulti = props.mode === "multiple" || props.mode === "tags";
  const allowClear =
    props.allowClear ??
    (isMulti
      ? {
          clearIcon: <IoIosClose color={COLORS.BUTTONS1} size={20} />,
        }
      : false);
  const optionRender =
    props.optionRender ??
    (props.mode === "multiple"
      ? (option: any) =>
          defaultMultiOptionRender(option, (props.value as any[]) ?? [])
      : undefined);
  const maxTagCount = props.maxTagCount ?? (isMulti ? "responsive" : undefined);
  const maxTagPlaceholder =
    props.maxTagPlaceholder ??
    (isMulti
      ? (omittedValues: any[]) => (
          <span className="plus">+{omittedValues.length}</span>
        )
      : undefined);
  const showOptionFullText = props.showOptionFullText ?? false;

  return (
    <>
      <SelectDropdownStyle />
      <StyledSelect
        {...rest}
        $themeMode={theme}
        $variant={resolvedVariant}
        className={`select-container select-${theme}-mode ${
          props.className || ""
        }`}
        placeholder={placeholder}
        disabled={disabled}
        suffixIcon={suffixIcon}
        allowClear={allowClear as any}
        optionRender={optionRender}
        maxTagCount={maxTagCount as any}
        maxTagPlaceholder={maxTagPlaceholder as any}
        popupMatchSelectWidth
        listHeight={props.listHeight ?? 300}
        virtual={props.virtual ?? false}
        getPopupContainer={() => document.body}
        popupClassName={`mydw-select-dropdown mydw-select-dropdown--${theme} ${
          resolvedVariant === "dashboard"
            ? "mydw-select-dropdown--dashboard"
            : ""
        } ${
          showOptionFullText ? "mydw-select-dropdown--full-text-options" : ""
        }`}
        dropdownStyle={{ zIndex: resolvedVariant === "dashboard" ? 78 : 9999 }}
      />
    </>
  );
};

export default SelectComponent;
